import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import FadeIn from "@/components/FadeIn";
import SeoHead from "@/components/SeoHead";
import InzichtenLinkBlock from "@/components/InzichtenLinkBlock";

const dimensions = [
  {
    n: "01",
    title: "Gebruik en doel",
    text:
      "Waarvoor wordt het systeem ingezet, door wie en in welke context? Bestuurlijke duiding van het werkelijke gebruik, ook als dat afwijkt van wat oorspronkelijk was bedoeld.",
  },
  {
    n: "02",
    title: "Data en gevoeligheid",
    text:
      "Welke persoonsgegevens, vertrouwelijke gegevens of gevoelige data worden verwerkt? Hoe is classificatie en bewaartermijn belegd?",
  },
  {
    n: "03",
    title: "Toegang en datastromen",
    text:
      "Wie kan erbij, waar gaat data naartoe en welke koppelingen bestaan met andere systemen of organisaties?",
  },
  {
    n: "04",
    title: "Jurisdictie en leveranciersmacht",
    text:
      "Welke leverancier is betrokken, onder welk recht en met welke afhankelijkheden? Wat zijn de gevolgen voor extraterritoriale toegang en bestuurlijke ruimte? Waar Europese alternatieven beschikbaar en passend zijn, brengen wij die mee in beeld als onderdeel van de bestuurlijke risicoafweging.",
  },
  {
    n: "05",
    title: "Transparantie en uitlegbaarheid",
    text:
      "Kan de organisatie uitleggen wat het systeem doet en welke invloed het heeft op besluiten over burgers, huurders, studenten of cliënten?",
  },
  {
    n: "06",
    title: "Menselijk toezicht en governance",
    text:
      "Wie beslist, wie controleert, wie grijpt in en wie is bestuurlijk eigenaar? Is governance werkbaar belegd of slechts op papier?",
  },
  {
    n: "07",
    title: "Lock in en continuïteit",
    text:
      "Kan de organisatie overstappen, herstellen of tijdelijk zonder het systeem functioneren? Welke afhankelijkheden raken de continuïteit van dienstverlening?",
  },
  {
    n: "08",
    title: "Proportionaliteit en digitale voetafdruk",
    text:
      "Is het gebruik van AI of automatisering nodig, proportioneel en verantwoord gezien energiegebruik, maatschappelijke impact en bestuurlijke waarde? Een bestuurlijke duiding, geen formele CO2 audit.",
  },
];

const scoring = [
  {
    color: "#1F7A4C",
    bg: "#E6F2EB",
    label: "Groen",
    text: "Aantoonbaar in control. Beleid, registers en rollen zijn op orde en worden periodiek geëvalueerd.",
  },
  {
    color: "#B25A15",
    bg: "#FCEFDE",
    label: "Oranje",
    text:
      "Ten dele in control. Belangrijke elementen zijn aanwezig, maar borging, volledigheid of actualiteit ontbreken.",
  },
  {
    color: "#A0241C",
    bg: "#F7E0DE",
    label: "Rood",
    text:
      "Niet in control. Bestuurlijk zicht ontbreekt of risico's zijn te groot om onbehandeld te laten.",
  },
];

export default function Methode() {
  return (
    <div data-testid="page-methode" className="pt-[96px]">
      <SeoHead
        title="De SovEU Assurance Method 2.0"
        description="De SovEU Assurance Method beoordeelt AI gebruik, algoritmen, cloudservices, datastromen, governance, jurisdictie, continuïteit en digitale voetafdruk."
        path="/methode"
      />
      <section className="section bg-white">
        <div className="container-soveu">
          <FadeIn className="max-w-3xl">
            <div className="eyebrow mb-4">Werkwijze</div>
            <h1 className="h1-display mb-6">De SovEU Regiemethode</h1>
            <p className="body-lg text-[#334155]">
              De SovEU Regiemethode is een gestructureerde, bestuurlijk bruikbare aanpak om grip te krijgen op AI, cloud en data. De methode combineert een vaste beoordelingsstructuur met acht dimensies, een heldere kleurscore en een bestuurlijke vertaling die direct gebruikt kan worden in besluitvorming.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Dimensions */}
      <section
        className="section"
        style={{ backgroundColor: "#F5F7FA" }}
        data-testid="methode-dimensions"
      >
        <div className="container-soveu">
          <FadeIn className="max-w-3xl mb-12">
            <h2 className="h2-display mb-4">De acht SovEU dimensies</h2>
            <p className="text-[#334155] body-lg">
              Elke beoordeling — van Bestuursscan tot Regiebeeld — wordt gemaakt langs acht vaste dimensies. Zo ontstaat een consistent beeld, ook over de jaren heen, en blijft het rapport bestuurlijk bruikbaar.
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dimensions.map((d, i) => (
              <FadeIn key={d.n} delay={i * 60}>
                <article
                  className="soveu-card p-7 h-full"
                  data-testid={`dimension-${d.n}`}
                >
                  <div
                    className="text-[13px] font-semibold mb-3"
                    style={{ color: "#2D6F8F" }}
                  >
                    Dimensie {d.n}
                  </div>
                  <h3 className="h3-display mb-3">{d.title}</h3>
                  <p className="text-[#334155] leading-relaxed text-[15px]">
                    {d.text}
                  </p>
                </article>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Scoring */}
      <section className="section bg-white" data-testid="methode-scoring">
        <div className="container-soveu">
          <FadeIn className="max-w-3xl mb-10">
            <h2 className="h2-display mb-4">Beoordelingssystematiek</h2>
            <p className="text-[#334155] body-lg">
              Elke dimensie krijgt een kleurscore. De kleur vertelt in één oogopslag of bestuurlijke regie aantoonbaar is, of dat er actie nodig is.
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-6">
            {scoring.map((s, i) => (
              <FadeIn key={s.label} delay={i * 100}>
                <div
                  className="soveu-card p-7 h-full"
                  data-testid={`scoring-${s.label.toLowerCase()}`}
                >
                  <div
                    className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[13px] font-semibold mb-4"
                    style={{ backgroundColor: s.bg, color: s.color }}
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: s.color }}
                    />
                    {s.label}
                  </div>
                  <p className="text-[#334155] leading-relaxed">{s.text}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section
        className="section"
        style={{ backgroundColor: "#F5F7FA" }}
        data-testid="methode-closing"
      >
        <div className="container-soveu max-w-3xl text-center">
          <FadeIn>
            <h2 className="h2-display mb-5">
              Wilt u de methode op uw organisatie laten toepassen?
            </h2>
            <p className="body-lg text-[#334155] mb-8">
              Plan een gesprek over de Bestuursscan AI en Data of het Regiebeeld. We bespreken kort waar uw organisatie nu staat en welke stap logisch is.
            </p>
            <Link to="/contact" className="btn-primary" data-testid="methode-cta">
              Plan een gesprek
              <ArrowRight size={18} />
            </Link>
          </FadeIn>
        </div>
      </section>

      <InzichtenLinkBlock
        testid="methode-inzichten"
        title="Actuele inzichten"
        intro="Lees meer over veelvoorkomende bestuurlijke vragen rond algoritmen, AI gebruik, datalekken en digitale afhankelijkheid."
      />
    </div>
  );
}
